import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import { ArrowLeft, Inbox } from 'lucide-react';
import { DataTable, DataTablePagination } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { buttonVariants } from '@/components/ui/button';
import { useKeepPageInRange } from '@/hooks/useKeepPageInRange';
import { cn } from '@/lib/utils';
import { formatNumber } from '@/utils/number';
import {
  downloadPcmpMarketingBatchXlsx,
  pcmpMarketingBatchProposalsQueryOptions,
} from '../api/pcmpMarketing.api';
import { getPcmpMarketingColumns } from '../components/PcmpMarketingQueue/PcmpMarketingColumns';
import { PcmpMarketingRowActions } from '../components/PcmpMarketingQueue/PcmpMarketingRowActions';
import { PcmpExportButton } from '../components/PcmpQueue/PcmpExportButton';
import { PcmpQueueFilterBar } from '../components/PcmpQueue/PcmpQueueFilterBar';
import { PCMP_QUEUE_PAGE_SIZE_OPTIONS } from '../constants/pcmpQueue.constants';
import { toPcmpQueueSearch } from '../hooks/usePcmpQueueFilters';
import { usePcmpMarketingBatchFilters } from '../hooks/usePcmpMarketingBatchFilters';
import {
  PCMP_MARKETING_BATCHES_TAB,
  type PcmpMarketingQueueItem,
  type PcmpMarketingQueueSearch,
} from '../types/pcmpMarketing.types';

const columns = getPcmpMarketingColumns({
  showMisColumn: false,
  // A batch's rows are listed as All Proposals lists them.
  renderActions: (item) => <PcmpMarketingRowActions item={item} tab="ALL" />,
});

/**
 * One route batch from the Proposals page's Batches tab (legacy View Items):
 * the viewer's proposals in it under the filters, with its Excel (LDS-820).
 */
export function PcmpMarketingBatchPage() {
  const router = useRouter();
  const controller = usePcmpMarketingBatchFilters();
  const { batchNo, params, appliedFilters, limit } = controller;

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pcmpMarketingBatchProposalsQueryOptions(batchNo, params),
  );

  useKeepPageInRange(params.page, data?.meta.totalPages, controller.setPage);

  // Back to the Batches tab under the same filters.
  const backSearch = useMemo<PcmpMarketingQueueSearch>(
    () => ({ ...toPcmpQueueSearch(appliedFilters), tab: PCMP_MARKETING_BATCHES_TAB }),
    [appliedFilters],
  );

  const openProposal = useCallback(
    (item: PcmpMarketingQueueItem) => {
      void router.navigate({
        to: '/pcmp/proposals/$proposalId',
        params: { proposalId: item.proposalId },
      });
    },
    [router],
  );

  return (
    <div className="flex flex-col gap-4 lg:h-[calc(100dvh-88px)]">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <Link
            to="/pcmp/marketing"
            search={backSearch}
            className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'w-fit gap-1.5')}
          >
            <ArrowLeft className="size-3.5" />
            Batches
          </Link>
          <h1 className="text-2xl font-semibold">Batch #{batchNo}</h1>
          <span className="text-sm text-muted-foreground" aria-live="polite">
            {data ? `${formatNumber(data.meta.total)} of your proposals` : ' '}
          </span>
        </div>
        <PcmpExportButton
          title={`Batch #${batchNo}: your proposals in it, under the filters below`}
          download={(date) => downloadPcmpMarketingBatchXlsx(batchNo, appliedFilters, date)}
        />
      </div>

      <PcmpQueueFilterBar controller={controller} idPrefix="pcmp-mkt-batch" showPayoutFilters />

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle={`Could not load batch #${batchNo}`}
          onRetry={() => void refetch()}
          isRetrying={isFetching}
          fill
        />
      ) : (
        <DataTable
          data={data?.data ?? []}
          columns={columns}
          isLoading={isLoading}
          stickyFirstColumn
          fillHeight
          className={cn('lg:min-h-0 lg:flex-1', isFetching && !isLoading && 'opacity-70')}
          emptyIcon={Inbox}
          emptyMessage="None of your proposals in this batch match the filters"
          skeletonRowCount={Math.min(limit, 15)}
          onRowClick={openProposal}
        />
      )}

      {data?.meta && !error ? (
        <DataTablePagination
          page={data.meta.page}
          totalPages={data.meta.totalPages}
          total={data.meta.total}
          limit={data.meta.limit}
          onPageChange={controller.setPage}
          limitSelect={{ options: PCMP_QUEUE_PAGE_SIZE_OPTIONS, onChange: controller.setLimit }}
        />
      ) : null}
    </div>
  );
}
